import { User } from '@/types/User';
import { IsUserAClient, IsUserAnAdmin, UserHasPermission } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';

export enum Category {
  Property = 'Property',
  Casualty = 'Casualty',
  FLP = 'FLP',
  Captives = 'Captives',
  Other = 'Other',
  Advisory = 'Advisory', // Advisory/Marketing are the same
  Cyber = 'Cyber',
  Production = 'Production', // Reporting Category
  Operations = 'Operations', // Reporting Category
  DataQuality = 'DataQuality', // Reporting Category
}

export enum UploadType {
  TableauDashboard = 'tableau-dashboard',
  VolatilityModel = 'volatility-model',
  StockDropModel = 'stock-drop-model',
  Document = 'document',
  ClientDocument = 'client-document',
  MarketingDocument = 'marketing-document',
  CyberModel = 'cyber-model',
}

/**
 * Get the user's upload types available for the selected category
 */
export function getUploadTypesForCategory(user: User, selectedCategory: Category): UploadType[] {
  const uploadTypesByCategory = new Map<Category | keyof typeof Category, UploadType[]>([
    [Category.Property, [UploadType.TableauDashboard, UploadType.Document]],
    [Category.Casualty, [UploadType.TableauDashboard, UploadType.Document]],
    [Category.FLP, [UploadType.TableauDashboard, UploadType.StockDropModel, UploadType.Document]],
    [Category.Captives, [UploadType.Document]],
    [Category.Other, [UploadType.VolatilityModel, UploadType.Document]],
    [Category.Advisory, [UploadType.TableauDashboard, UploadType.Document]],
    [Category.Cyber, [UploadType.CyberModel, UploadType.Document, UploadType.TableauDashboard]],
    [Category.Production, [UploadType.TableauDashboard, UploadType.Document]],
    [Category.Operations, [UploadType.TableauDashboard, UploadType.Document]],
    [Category.DataQuality, [UploadType.TableauDashboard, UploadType.Document]],
  ]);

  // If the user is not an admin or does not have write permissions, remove the Document,
  // TableauDashboard, and StockDropModel upload types
  if (!IsUserAnAdmin(user) && !UserHasPermission(user, UserPermission.AnalyticsWrite)) {
    for (const category of uploadTypesByCategory.keys()) {
      const uploadTypes = uploadTypesByCategory.get(category);
      uploadTypesByCategory.set(
        category,
        uploadTypes?.filter((type) => ![UploadType.Document, UploadType.TableauDashboard, UploadType.StockDropModel].includes(type)) || [],
      );
    }
  }

  // Clients can edit existing Volatility Models (with analytics:read) but cannot create new ones
  if (IsUserAClient(user)) {
    for (const category of uploadTypesByCategory.keys()) {
      const uploadTypes = uploadTypesByCategory.get(category);
      uploadTypesByCategory.set(
        category,
        uploadTypes?.filter((type) => type !== UploadType.VolatilityModel) || [],
      );
    }
  }

  return uploadTypesByCategory.get(selectedCategory) || [];
}

/**
 * Checks if the user has any upload options available for the selected category
 */
export function hasAnyUploadOptions(user: User, selectedCategory: Category | keyof typeof Category): boolean {
  return getUploadTypesForCategory(user, selectedCategory as Category).length > 0;
}

export function getTypeLabel(name: string, type: UploadType): string {
  switch (type) {
    case UploadType.TableauDashboard:
      if (name.includes('Cat')) {
        return '(Dashboard)';
      }
      break;
    case UploadType.Document:
      if (name.includes('Cat')) {
        return '(Document)';
      }
      break;
  }
  return '';
}
